import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, FileText, FolderKanban, MessageSquare, Clock } from 'lucide-react';
import { api } from '../services/api';
import type { SearchResult, SearchResultType } from '../types';

function sanitizeHighlight(html: string): string {
  const div = document.createElement('div');
  div.textContent = html;
  let safe = div.innerHTML;
  safe = safe
    .replace(/&lt;mark&gt;/g, '<mark>')
    .replace(/&lt;\/mark&gt;/g, '</mark>');
  return safe;
}

const TYPE_CONFIG: Record<SearchResultType, { labelKey: string; icon: typeof FileText; color: string }> = {
  ticket: { labelKey: 'search.tickets', icon: FileText, color: 'text-info-600 bg-info-50' },
  project: { labelKey: 'search.projects', icon: FolderKanban, color: 'text-purple-600 bg-purple-50' },
  comment: { labelKey: 'search.comments', icon: MessageSquare, color: 'text-success-600 bg-success-50' },
  worklog: { labelKey: 'search.workLogs', icon: Clock, color: 'text-orange-600 bg-orange-50' },
};

function getNavigationPath(result: SearchResult): string {
  switch (result.type) {
    case 'ticket':
    case 'comment':
    case 'worklog':
      return `/tickets/${result.id}`;
    case 'project':
      return `/projects/${result.id}`;
    default:
      return '/';
  }
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totals, setTotals] = useState<Record<SearchResultType, number>>({ ticket: 0, project: 0, comment: 0, worklog: 0 });
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
    if (!open) {
      setQuery('');
      setResults([]);
      setTotals({ ticket: 0, project: 0, comment: 0, worklog: 0 });
      setSearchError(false);
    }
  }, [open]);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setTotals({ ticket: 0, project: 0, comment: 0, worklog: 0 });
      setSearchError(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    try {
      const res = await api.search({ q });
      if (res.data) {
        setResults(res.data.results);
        setTotals(res.data.totals);
        setSelectedIndex(0);
      }
    } catch {
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    navigate(getNavigationPath(result));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  useEffect(() => {
    const el = dropdownRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const grouped = results.reduce<Record<SearchResultType, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<SearchResultType, SearchResult[]>);

  let globalIdx = -1;

  const { t } = useTranslation();

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t('aria.globalSearch')}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-surface-500 bg-surface-100 hover:bg-surface-200 rounded-lg transition-colors"
      >
        <Search size={16} />
        <span className="hidden md:inline">{t('search.placeholder')}</span>
        <kbd className="hidden md:inline text-xs bg-surface-0/80 px-1.5 py-0.5 rounded border border-surface-400/15 font-mono">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[15vh]">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div role="dialog" aria-label={t('aria.globalSearch')} aria-modal="true" className="relative w-full max-w-2xl modal-content overflow-hidden" dir="rtl">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-400/10">
              <Search size={20} className="text-surface-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('search.inputPlaceholder')}
                className="flex-1 bg-transparent outline-none text-base"
              />
              {query && (
                <button onClick={() => { setQuery(''); setResults([]); }} className="p-1 hover:bg-surface-100 rounded" aria-label={t('aria.clearSearch')}>
                  <X size={16} className="text-surface-400" />
                </button>
              )}
              <kbd className="text-xs bg-surface-100 px-1.5 py-0.5 rounded border text-surface-400 font-mono">ESC</kbd>
            </div>

            {loading && (
              <div className="px-4 py-8 text-center text-surface-400 text-sm">{t('search.searching')}</div>
            )}

            {!loading && searchError && (
              <div className="px-4 py-8 text-center text-danger-500 text-sm">{t('search.searchError')}</div>
            )}

            {!loading && !searchError && query.length >= 2 && results.length === 0 && (
              <div className="px-4 py-8 text-center text-surface-400 text-sm">{t('search.noResults')}</div>
            )}

            {!loading && results.length > 0 && (
              <div ref={dropdownRef} className="max-h-[50vh] overflow-y-auto py-2">
                {(Object.keys(grouped) as SearchResultType[]).map(type => {
                  const cfg = TYPE_CONFIG[type];
                  const Icon = cfg.icon;
                  const items = grouped[type];
                  return (
                    <div key={type}>
                      <div className="px-4 py-1.5 text-xs font-medium text-surface-400 uppercase flex items-center gap-2">
                        <Icon size={14} />
                        {t(cfg.labelKey)} ({totals[type]})
                      </div>
                      {items.map(result => {
                        globalIdx++;
                        const idx = globalIdx;
                        return (
                          <button
                            key={`${result.type}-${result.id}-${idx}`}
                            data-index={idx}
                            onClick={() => handleSelect(result)}
                            className={`w-full text-right px-4 py-2.5 flex items-start gap-3 transition-colors ${
                              idx === selectedIndex ? 'bg-primary-50' : 'hover:bg-surface-50'
                            }`}
                          >
                            <div className={`mt-0.5 p-1.5 rounded-lg ${cfg.color}`}>
                              <Icon size={14} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-surface-900 truncate">{result.title}</div>
                              <div
                                className="text-xs text-surface-500 mt-0.5 line-clamp-1"
                                dangerouslySetInnerHTML={{ __html: sanitizeHighlight(result.highlight) }}
                              />
                              {result.breadcrumb && (
                                <div className="text-xs text-surface-400 mt-0.5">{result.breadcrumb}</div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {query.length < 2 && !loading && (
              <div className="px-4 py-6 text-center text-surface-400 text-sm">
                {t('search.minChars')}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
